import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiLayers,
  FiPlus,
  FiSearch,
  FiUser,
  FiPhone,
  FiMapPin,
  FiArrowUpRight,
  FiArrowDownLeft,
  FiTrash2,
  FiEdit2,
  FiRefreshCw,
  FiDollarSign,
  FiCalendar,
  FiFileText,
  FiX,
  FiCheckCircle,
} from "react-icons/fi";
import { Modal, Button, Form, Spinner, Tabs, Tab } from "react-bootstrap";
import { toast } from "react-toastify";
import ToolsHeader from "./ToolsHeader";
import {
  listProjects,
  addProject,
  editProject,
  deleteProject,
  getOverallProjectsLedgerSummary,
  getProjectById,
  getProjectLedgerSummary,
  getProjectLedgersList,
  addProjectLedgerEntry,
  editProjectLedgerEntry,
  deleteProjectLedgerEntry,
  getProjectNotes,
  addProjectNote,
  editProjectNote,
  deleteProjectNote,
} from "../../api/tools/toolsApi";
import { PROJECTS_HOME, useFullPageForms } from "./forms/toolFormHelpers";
import { ToolsSkeleton, ToolsLoadError } from "./ToolsState";
import {
  assertSuccess,
  errorText,
  formatLedgerDate,
  isCreditEntry,
  ledgerDateInput,
  toLedgerApiDate,
} from "./forms/projectModel";
import "./Tools.css";
import { formatDate } from "../../utils/date";

const ProjectLedgerTool = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Under /partner/tools the add/edit forms are full pages; elsewhere they stay popups
  const fullPageForms = useFullPageForms();
  const preferredProjectId = searchParams.get("project");
  const preferredTab = searchParams.get("tab");
  const reopenedProject = useRef(false);
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token =
    loggedInUser?.refreshToken ||
    authState?.refreshToken ||
    loggedInUser?.token ||
    authState?.token;

  // Projects & Overall Summary State
  const [projects, setProjects] = useState([]);
  const [overallSummary, setOverallSummary] = useState(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Selected Project & Details State
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectLedgerSummary, setProjectLedgerSummary] = useState(null);
  const [ledgers, setLedgers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [activeTab, setActiveTab] = useState(preferredTab === "notes" ? "notes" : "ledgers"); // ledgers, notes, overview

  // Modals
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState({
    id: null,
    project_name: "",
    client_name: "",
    client_mobile: "",
    address: "",
    description: "",
  });
  const [submittingProject, setSubmittingProject] = useState(false);

  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerForm, setLedgerForm] = useState({
    id: null,
    type: "CREDIT",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    remark: "",
  });
  const [submittingLedger, setSubmittingLedger] = useState(false);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteForm, setNoteForm] = useState({ id: null, title: "", note: "" });
  const [submittingNote, setSubmittingNote] = useState(false);

  // 1. Fetch Projects and Overall Ledger Summary
  const fetchProjectsAndSummary = useCallback(async () => {
    if (!token) return;
    setLoadingProjects(true);
    setLoadError(false);
    try {
      const [projRes, summaryRes] = await Promise.allSettled([
        listProjects(token, { page_number: 1, page_size: 10000 }),
        getOverallProjectsLedgerSummary(token),
      ]);

      if (projRes.status === "fulfilled" && projRes.value?.statusType && projRes.value.statusType !== "SUCCESS") {
        setProjects([]);
        setLoadError(true);
      } else if (projRes.status === "fulfilled") {
        setProjects(projRes.value?.projects || []);
      } else {
        setProjects([]);
        setLoadError(true);
      }

      if (summaryRes.status === "fulfilled") {
        setOverallSummary(summaryRes.value || null);
      } else {
        setOverallSummary(null);
      }
    } catch (err) {
      console.error("Error fetching projects and summary:", err);
      toast.error(errorText(err, "Failed to load projects"));
    } finally {
      setLoadingProjects(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProjectsAndSummary();
  }, [fetchProjectsAndSummary]);

  // 2. Fetch Selected Project Details, Ledgers, Notes & Summary
  const fetchProjectDetails = useCallback(
    async (projectId) => {
      if (!token || !projectId) return;
      setLoadingDetails(true);
      try {
        const [detailsRes, summaryRes, ledgersRes, notesRes] =
          await Promise.allSettled([
            getProjectById(token, projectId),
            getProjectLedgerSummary(token, projectId),
            getProjectLedgersList(token, projectId),
            getProjectNotes(token, projectId),
          ]);

        if (detailsRes.status === "fulfilled") {
          const det = detailsRes.value?.project;
          if (det && det.id) {
            setSelectedProject((prev) => ({ ...prev, ...det }));
          }
        }

        if (summaryRes.status === "fulfilled") {
          setProjectLedgerSummary(summaryRes.value || null);
        } else {
          setProjectLedgerSummary(null);
        }

        if (ledgersRes.status === "fulfilled") {
          setLedgers(ledgersRes.value?.projectLedgers || []);
        } else {
          setLedgers([]);
        }

        if (notesRes.status === "fulfilled") {
          setNotes(notesRes.value?.projectNotes || []);
        } else {
          setNotes([]);
        }
      } catch (err) {
        console.error("Error fetching project details:", err);
      } finally {
        setLoadingDetails(false);
      }
    },
    [token]
  );

  const handleOpenProject = (proj) => {
    setSelectedProject(proj);
    fetchProjectDetails(proj.id);
  };

  // Coming back from a form: reopen that project (on the tab the form belongs to)
  useEffect(() => {
    if (!preferredProjectId || reopenedProject.current || projects.length === 0) return;
    const match = projects.find((p) => String(p.id) === preferredProjectId);
    if (match) {
      reopenedProject.current = true;
      handleOpenProject(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, preferredProjectId]);

  // Open a form: a full page under /partner/tools, otherwise the popup
  const emptyProject = { id: null, project_name: "", client_name: "", client_mobile: "", address: "", description: "" };

  const openProjectForm = (project = null) => {
    if (fullPageForms) {
      navigate(project ? `${PROJECTS_HOME}/${project.id}/edit` : `${PROJECTS_HOME}/new`, project ? { state: { project } } : undefined);
      return;
    }
    setProjectForm(
      project
        ? {
            id: project.id,
            project_name: project.project_name || "",
            client_name: project.client_name || "",
            client_mobile: project.client_mobile || "",
            address: project.client_address || "",
            description: project.description || "",
          }
        : emptyProject
    );
    setShowProjectModal(true);
  };

  const openLedgerForm = ({ entry = null, type = "CREDIT" } = {}) => {
    if (fullPageForms) {
      if (!selectedProject?.id) return;
      const base = `${PROJECTS_HOME}/${selectedProject.id}/ledger`;
      navigate(entry ? `${base}/${entry.id}/edit` : `${base}/new?type=${type}`, entry ? { state: { ledger: entry } } : undefined);
      return;
    }
    setLedgerForm(
      entry
        ? {
            id: entry.id,
            type,
            amount: entry.amount,
            date: ledgerDateInput(entry.transaction_date),
            remark: entry.description || "",
            original: entry.transaction_date,
          }
        : { id: null, type, amount: "", date: new Date().toISOString().split("T")[0], remark: "", original: null }
    );
    setShowLedgerModal(true);
  };

  const openNoteForm = (note = null) => {
    if (fullPageForms) {
      if (!selectedProject?.id) return;
      const base = `${PROJECTS_HOME}/${selectedProject.id}/notes`;
      navigate(note ? `${base}/${note.id}/edit` : `${base}/new`, note ? { state: { note } } : undefined);
      return;
    }
    setNoteForm(note ? { id: note.id, title: note.title || "", note: note.content || "" } : { id: null, title: "", note: "" });
    setShowNoteModal(true);
  };

  // 3. Add or Edit Project
  const handleSaveProject = async (e) => {
    e.preventDefault();
    if (!projectForm.project_name.trim() || !projectForm.client_name.trim()) {
      toast.warning("Please enter the project name and the client name");
      return;
    }
    setSubmittingProject(true);
    try {
      const body = {
        project_name: projectForm.project_name,
        client_name: projectForm.client_name,
        client_mobile: projectForm.client_mobile,
        client_address: projectForm.address,
        description: projectForm.description,
      };
      if (projectForm.id) {
        assertSuccess(await editProject(token, { id: projectForm.id, ...body }), "Failed to save project");
        toast.success("Project updated successfully");
      } else {
        const res = assertSuccess(await addProject(token, body), "Failed to save project");
        toast.success("Project created successfully");
        const created = res?.project;
        if (created?.id) handleOpenProject(created);
      }
      setShowProjectModal(false);
      setProjectForm(emptyProject);
      fetchProjectsAndSummary();
    } catch (err) {
      console.error("Error saving project:", err);
      toast.error(errorText(err, "Failed to save project"));
    } finally {
      setSubmittingProject(false);
    }
  };

  // 4. Delete Project
  const handleDeleteProject = async (projectId, e) => {
    e?.stopPropagation();
    if (
      !window.confirm(
        "Are you sure you want to delete this project, its ledgers, and notes?"
      )
    )
      return;
    try {
      assertSuccess(await deleteProject(token, projectId), "Failed to delete project");
      toast.success("Project deleted");
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
      fetchProjectsAndSummary();
    } catch (err) {
      console.error("Error deleting project:", err);
      toast.error(errorText(err, "Failed to delete project"));
    }
  };

  // 5. Add or Edit Project Ledger Entry
  const handleSaveLedgerEntry = async (e) => {
    e.preventDefault();
    if (!selectedProject?.id) return;
    if (!ledgerForm.amount || Number(ledgerForm.amount) <= 0) {
      toast.warning("Please enter a valid amount");
      return;
    }
    setSubmittingLedger(true);
    try {
      const body = {
        project_id: selectedProject.id,
        type: ledgerForm.type,
        amount: Number(ledgerForm.amount),
        transaction_date: toLedgerApiDate(ledgerForm.date, ledgerForm.original),
        description: ledgerForm.remark,
      };
      if (ledgerForm.id) {
        assertSuccess(await editProjectLedgerEntry(token, { id: ledgerForm.id, ...body }), "Failed to save ledger entry");
        toast.success("Ledger entry updated");
      } else {
        assertSuccess(await addProjectLedgerEntry(token, body), "Failed to save ledger entry");
        toast.success("Ledger entry added");
      }
      setShowLedgerModal(false);
      setLedgerForm({
        id: null,
        type: "CREDIT",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        remark: "",
        original: null,
      });
      fetchProjectDetails(selectedProject.id);
      fetchProjectsAndSummary();
    } catch (err) {
      console.error("Error saving project ledger:", err);
      toast.error(errorText(err, "Failed to save ledger entry"));
    } finally {
      setSubmittingLedger(false);
    }
  };

  // 6. Delete Project Ledger Entry
  const handleDeleteLedgerEntry = async (ledgerId) => {
    if (!window.confirm("Are you sure you want to delete this ledger entry?"))
      return;
    try {
      assertSuccess(await deleteProjectLedgerEntry(token, ledgerId), "Failed to delete entry");
      toast.success("Ledger entry deleted");
      if (selectedProject?.id) {
        fetchProjectDetails(selectedProject.id);
        fetchProjectsAndSummary();
      }
    } catch (err) {
      console.error("Error deleting ledger entry:", err);
      toast.error(errorText(err, "Failed to delete entry"));
    }
  };

  // 7. Add or Edit Project Note
  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!selectedProject?.id) return;
    if (!noteForm.title.trim() || !noteForm.note.trim()) {
      toast.warning("Please enter the note title and content");
      return;
    }
    setSubmittingNote(true);
    try {
      const body = { project_id: selectedProject.id, title: noteForm.title, content: noteForm.note };
      if (noteForm.id) {
        assertSuccess(await editProjectNote(token, { id: noteForm.id, ...body }), "Failed to save note");
        toast.success("Project note updated");
      } else {
        assertSuccess(await addProjectNote(token, body), "Failed to save note");
        toast.success("Project note added");
      }
      setShowNoteModal(false);
      setNoteForm({ id: null, title: "", note: "" });
      fetchProjectDetails(selectedProject.id);
    } catch (err) {
      console.error("Error saving note:", err);
      toast.error(errorText(err, "Failed to save note"));
    } finally {
      setSubmittingNote(false);
    }
  };

  // 8. Delete Project Note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      assertSuccess(await deleteProjectNote(token, noteId), "Failed to delete note");
      toast.success("Note deleted");
      if (selectedProject?.id) fetchProjectDetails(selectedProject.id);
    } catch (err) {
      console.error("Error deleting note:", err);
      toast.error(errorText(err, "Failed to delete note"));
    }
  };

  // Filtered projects
  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    const q = searchQuery.toLowerCase();
    return projects.filter(
      (p) =>
        (p.project_name && p.project_name.toLowerCase().includes(q)) ||
        (p.client_name && p.client_name.toLowerCase().includes(q)) ||
        (p.client_address && p.client_address.toLowerCase().includes(q)) ||
        (p.client_mobile && p.client_mobile.includes(q))
    );
  }, [projects, searchQuery]);

  // Overall calculations
  const overallCredit = Number(overallSummary?.totalCredit || 0);
  const overallDebit = Number(overallSummary?.totalDebit || 0);
  const overallBalance = Number(overallSummary?.totalBalance ?? overallCredit - overallDebit);

  // Single project calculations
  const projectCredit = Number(projectLedgerSummary?.totalCredit || 0);
  const projectDebit = Number(projectLedgerSummary?.totalDebit || 0);
  const projectBalance = Number(projectLedgerSummary?.totalBalance ?? projectCredit - projectDebit);

  return (
    <div className="tools-container">
      <ToolsHeader
        title="Ledger & Project Management Tool"
        subtitle="Manage interior & renovation projects, track customer advances, material expenses, and site notes."
        actions={
          <div className="d-flex align-items-center gap-2">
            <button
              className="tools-btn tools-btn--secondary"
              onClick={fetchProjectsAndSummary}
              disabled={loadingProjects}
            >
              <FiRefreshCw className={loadingProjects ? "spin" : ""} /> Refresh
            </button>
            <button
              className="tools-btn tools-btn--primary"
              onClick={() => {
                openProjectForm();
              }}
            >
              <FiPlus /> New Project
            </button>
          </div>
        }
      />

      {loadError && <ToolsLoadError text="Couldn't load your projects." onRetry={fetchProjectsAndSummary} />}

      {/* Overall Ledger Summary Stats */}
      <div className="tools-stats-grid">
        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--green">
            <FiArrowDownLeft />
          </div>
          <div>
            <div className="tools-stat-card__label">Total Received (Advances)</div>
            <div className="tools-stat-card__value text-success">
              ₹{Number(overallCredit).toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--red">
            <FiArrowUpRight />
          </div>
          <div>
            <div className="tools-stat-card__label">Total Expenses (Paid Out)</div>
            <div className="tools-stat-card__value text-danger">
              ₹{Number(overallDebit).toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--blue">
            <FiDollarSign />
          </div>
          <div>
            <div className="tools-stat-card__label">Net Projects Balance</div>
            <div
              className={`tools-stat-card__value ${
                overallBalance >= 0 ? "text-success" : "text-danger"
              }`}
            >
              ₹{Number(overallBalance).toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        <div className="tools-stat-card">
          <div className="tools-stat-card__icon tools-stat-card__icon--purple">
            <FiLayers />
          </div>
          <div>
            <div className="tools-stat-card__label">Total Projects</div>
            <div className="tools-stat-card__value text-dark">{projects.length}</div>
          </div>
        </div>
      </div>

      {/* Search & Action Bar */}
      <div className="tools-action-bar">
        <div className="tools-search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search projects by name, client, mobile or address..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <span className="text-muted small">
          Showing {filteredProjects.length} of {projects.length} project(s)
        </span>
      </div>

      {/* Projects Grid / Table */}
      {loadingProjects ? (
        <ToolsSkeleton label="Loading projects" />
      ) : filteredProjects.length === 0 ? (
        <div className="tools-empty-state">
          <div className="tools-empty-state__icon">
            <FiLayers />
          </div>
          <h4>No Projects Found</h4>
          <p>Create your first interior design, renovation, or construction project.</p>
          <button
            className="tools-btn tools-btn--primary"
            onClick={() => openProjectForm()}
          >
            <FiPlus /> Create Project
          </button>
        </div>
      ) : (
        <div className="row g-3 mb-4">
          {filteredProjects.map((p) => (
            <div className="col-12 col-md-6 col-lg-4" key={p.id}>
              <div
                className="tool-hub-card h-100"
                style={{ cursor: "pointer" }}
                onClick={() => handleOpenProject(p)}
              >
                <div>
                  <div className="tool-hub-card__header">
                    <div className="tool-hub-card__icon-box tool-hub-card__icon-box--indigo">
                      <FiLayers />
                    </div>
                    <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="tools-btn--icon"
                        title="Edit Project"
                        onClick={() => openProjectForm(p)}
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        className="tools-btn--icon tools-btn--icon-danger"
                        title="Delete Project"
                        onClick={(e) => handleDeleteProject(p.id, e)}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <h5 className="fw-bold text-dark mb-1">{p.project_name}</h5>
                  {p.description && (
                    <p className="tool-hub-card__desc text-truncate mb-2">
                      {p.description}
                    </p>
                  )}

                  <div className="d-flex flex-column gap-1 my-3 small text-secondary">
                    {p.client_name && (
                      <div className="d-flex align-items-center gap-2">
                        <FiUser size={13} className="text-primary" />
                        <span className="fw-semibold text-dark">{p.client_name}</span>
                        {p.client_mobile && (
                          <span className="text-muted">({p.client_mobile})</span>
                        )}
                      </div>
                    )}
                    {p.client_address && (
                      <div className="d-flex align-items-center gap-2">
                        <FiMapPin size={13} className="text-muted" />
                        <span className="text-truncate">{p.client_address}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="tool-hub-card__footer">
                  <span>Manage Ledgers & Notes</span>
                  <span>View &rarr;</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project Details Drawer (Ledgers + Notes) */}
      {selectedProject && (
        <div className="tools-drawer-overlay" onClick={() => setSelectedProject(null)}>
          <div className="tools-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="tools-drawer__header">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <span className="tools-badge tools-badge--primary">Project #{selectedProject.id}</span>
                </div>
                <h4 className="fw-bold text-dark mb-0 mt-1">
                  {selectedProject.project_name}
                </h4>
              </div>
              <button
                className="tools-btn--icon"
                onClick={() => setSelectedProject(null)}
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="tools-drawer__body">
              {/* Project KPI Cards */}
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <div
                    style={{
                      background: "#ecfdf5",
                      borderRadius: 10,
                      padding: "10px",
                      border: "1px solid #a7f3d0",
                    }}
                  >
                    <div className="text-success small fw-semibold">Advance / In</div>
                    <div className="fw-bold text-success fs-6">
                      ₹{Number(projectCredit).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div
                    style={{
                      background: "#fef2f2",
                      borderRadius: 10,
                      padding: "10px",
                      border: "1px solid #fecaca",
                    }}
                  >
                    <div className="text-danger small fw-semibold">Expenses / Out</div>
                    <div className="fw-bold text-danger fs-6">
                      ₹{Number(projectDebit).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div
                    style={{
                      background: "#eff6ff",
                      borderRadius: 10,
                      padding: "10px",
                      border: "1px solid #bfdbfe",
                    }}
                  >
                    <div className="text-primary small fw-semibold">Net Balance</div>
                    <div
                      className={`fw-bold fs-6 ${
                        projectBalance >= 0 ? "text-primary" : "text-danger"
                      }`}
                    >
                      ₹{Number(projectBalance).toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs for Ledgers vs Notes vs Info */}
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3 border-bottom"
              >
                <Tab eventKey="ledgers" title={`Ledger Entries (${ledgers.length})`}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold text-dark mb-0">Project Transactions</h6>
                    <button
                      className="tools-btn tools-btn--sm tools-btn--primary"
                      onClick={() => openLedgerForm()}
                    >
                      <FiPlus /> Add Ledger Entry
                    </button>
                  </div>

                  {loadingDetails ? (
                    <ToolsSkeleton rows={3} label="Loading ledger entries" />
                  ) : ledgers.length === 0 ? (
                    <div className="tools-empty-state py-4">
                      <p className="mb-0">No ledger entries added for this project.</p>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {ledgers.map((item) => {
                        const isCredit = isCreditEntry(item);
                        return (
                          <div
                            key={item.id}
                            style={{
                              border: "1px solid #e2e8f0",
                              borderRadius: 10,
                              padding: "12px 14px",
                              background: "#ffffff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                            }}
                          >
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <span
                                  className={`tools-badge ${
                                    isCredit ? "tools-badge--credit" : "tools-badge--debit"
                                  }`}
                                >
                                  {isCredit ? "Payment In (Credit)" : "Expense Out (Debit)"}
                                </span>
                                <span className="text-muted small">
                                  {formatLedgerDate(item.transaction_date)}
                                </span>
                              </div>
                              {item.description && (
                                <div className="text-secondary small">{item.description}</div>
                              )}
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              <span
                                className={`fw-bold fs-6 ${
                                  isCredit ? "text-success" : "text-danger"
                                }`}
                              >
                                ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                              </span>
                              <button
                                className="tools-btn--icon"
                                title="Edit"
                                onClick={() => openLedgerForm({ entry: item, type: item.type || (isCredit ? "CREDIT" : "DEBIT") })}
                              >
                                <FiEdit2 size={13} />
                              </button>
                              <button
                                className="tools-btn--icon tools-btn--icon-danger"
                                title="Delete"
                                onClick={() => handleDeleteLedgerEntry(item.id)}
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Tab>

                <Tab eventKey="notes" title={`Site Notes (${notes.length})`}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold text-dark mb-0">Project & Site Notes</h6>
                    <button
                      className="tools-btn tools-btn--sm tools-btn--primary"
                      onClick={() => openNoteForm()}
                    >
                      <FiPlus /> Add Note
                    </button>
                  </div>

                  {loadingDetails ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" variant="primary" />
                    </div>
                  ) : notes.length === 0 ? (
                    <div className="tools-empty-state py-4">
                      <p className="mb-0">No site notes added yet.</p>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-2">
                      {notes.map((n) => (
                        <div
                          key={n.id}
                          style={{
                            border: "1px solid #e2e8f0",
                            borderRadius: 10,
                            padding: "12px 14px",
                            background: "#ffffff",
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 10,
                          }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <FiFileText className="text-primary mt-1 flex-shrink-0" />
                            <div>
                              <div className="fw-semibold text-dark">{n.title}</div>
                              <p className="mb-1 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                                {n.content}
                              </p>
                              <span className="text-muted small">
                                {formatDate(n.updated_date, { fallback: "Note" })}
                              </span>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-1">
                            <button
                              className="tools-btn--icon"
                              title="Edit Note"
                              onClick={() => openNoteForm(n)}
                            >
                              <FiEdit2 size={13} />
                            </button>
                            <button
                              className="tools-btn--icon tools-btn--icon-danger"
                              title="Delete Note"
                              onClick={() => handleDeleteNote(n.id)}
                            >
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Tab>

                <Tab eventKey="overview" title="Project Details">
                  <div className="p-3 bg-light rounded-3 border">
                    <div className="mb-2">
                      <strong className="text-dark">Client:</strong>{" "}
                      <span>{selectedProject.client_name || "N/A"}</span>
                    </div>
                    <div className="mb-2">
                      <strong className="text-dark">Mobile:</strong>{" "}
                      <span>{selectedProject.client_mobile || "N/A"}</span>
                    </div>
                    <div className="mb-2">
                      <strong className="text-dark">Address:</strong>{" "}
                      <span>{selectedProject.client_address || "N/A"}</span>
                    </div>
                    <div className="mb-2">
                      <strong className="text-dark">Description:</strong>{" "}
                      <span>{selectedProject.description || "N/A"}</span>
                    </div>
                  </div>
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create/Edit Project */}
      <Modal show={showProjectModal} onHide={() => setShowProjectModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {projectForm.id ? "Edit Project" : "Add New Project"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveProject}>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Project Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Villa 304 Interior"
                    value={projectForm.project_name}
                    onChange={(e) =>
                      setProjectForm({ ...projectForm, project_name: e.target.value })
                    }
                    required
                    autoFocus
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Client Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Rajesh Kumar"
                    value={projectForm.client_name}
                    onChange={(e) =>
                      setProjectForm({ ...projectForm, client_name: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Client Mobile</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="e.g. 9876543210"
                    value={projectForm.client_mobile}
                    onChange={(e) =>
                      setProjectForm({ ...projectForm, client_mobile: e.target.value })
                    }
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6">
                <Form.Group>
                  <Form.Label className="fw-semibold">Site Address / Location</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Sector 62, Noida"
                    value={projectForm.address}
                    onChange={(e) =>
                      setProjectForm({ ...projectForm, address: e.target.value })
                    }
                  />
                </Form.Group>
              </div>

              <div className="col-12">
                <Form.Group>
                  <Form.Label className="fw-semibold">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="e.g. Full home renovation, modular kitchen, electricals"
                    value={projectForm.description}
                    onChange={(e) =>
                      setProjectForm({ ...projectForm, description: e.target.value })
                    }
                  />
                </Form.Group>
              </div>

            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowProjectModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingProject}>
              {submittingProject ? "Saving..." : projectForm.id ? "Update Project" : "Create Project"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Add/Edit Project Ledger Entry */}
      <Modal show={showLedgerModal} onHide={() => setShowLedgerModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {ledgerForm.id ? "Edit Ledger Entry" : "Add Project Ledger Entry"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveLedgerEntry}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Entry Type *</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant={ledgerForm.type === "CREDIT" ? "success" : "outline-success"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setLedgerForm({ ...ledgerForm, type: "CREDIT" })}
                >
                  <FiArrowDownLeft /> Payment Received (Credit)
                </Button>
                <Button
                  variant={ledgerForm.type === "DEBIT" ? "danger" : "outline-danger"}
                  className="flex-grow-1"
                  type="button"
                  onClick={() => setLedgerForm({ ...ledgerForm, type: "DEBIT" })}
                >
                  <FiArrowUpRight /> Expense Paid (Debit)
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Amount (₹) *</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 25000"
                value={ledgerForm.amount}
                onChange={(e) =>
                  setLedgerForm({ ...ledgerForm, amount: e.target.value })
                }
                required
                min="1"
                autoFocus
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Date *</Form.Label>
              <Form.Control
                type="date"
                value={ledgerForm.date}
                onChange={(e) =>
                  setLedgerForm({ ...ledgerForm, date: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Remark / Details</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="e.g. Client advance payment, Payment to carpenter, Tile purchase"
                value={ledgerForm.remark}
                onChange={(e) =>
                  setLedgerForm({ ...ledgerForm, remark: e.target.value })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowLedgerModal(false)}>
              Cancel
            </Button>
            <Button
              variant={ledgerForm.type === "CREDIT" ? "success" : "danger"}
              type="submit"
              disabled={submittingLedger}
            >
              {submittingLedger ? "Saving..." : ledgerForm.id ? "Update Entry" : "Save Entry"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Add/Edit Project Note */}
      <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 fw-bold">
            {noteForm.id ? "Edit Note" : "Add Project Note"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveNote}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Title *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Kitchen tiles"
                value={noteForm.title}
                onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Note Content *</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="e.g. Client requested changes in kitchen tiles, meeting minutes, vendor commitments"
                value={noteForm.note}
                onChange={(e) =>
                  setNoteForm({ ...noteForm, note: e.target.value })
                }
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={() => setShowNoteModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submittingNote}>
              {submittingNote ? "Saving..." : noteForm.id ? "Update Note" : "Add Note"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectLedgerTool;
